<template>
    <div
        v-if="signatures.length > 0"
        :title="tooltip()"
        style="display: inline-block;padding:2px;cursor:pointer"
        @click="loadInfo"
    >
        <kgr-icon
            v-if="iconClass"
            :icon-class="iconClass"
            small
            :light="!completed"
            style="vertical-align: text-bottom"
            :class="{ 'no-signees-available': numAvailableSignees === 0 }"
        />

        <div
            v-for="signature in signatures"
            :key="signature.id"
            class="signature-status-box"
        >
            <div
                v-if="signature.signed"
                class="signature-status signature-status-signed"
            ></div>
            <div
                v-else-if="signature.declined"
                class="signature-status signature-status-declined"
            ></div>
            <div
                v-else
                class="signature-status signature-status-blank"
            ></div>
        </div>
        <kgr-icon
            v-if="numAvailableSignees === 0"
            icon-class="warning"
            highlight
            style="vertical-align: text-bottom"
            small
        />

        <kgr-generic-modal
            v-if="showInfo"
            :headline="modalHeadline()"
            :value="showInfo"
            :width-in-px="800"
            @close="closeInfo"
        >
            <kgr-loading v-if="signatures === null" />
            <span
                v-else
            >
                <br />
                <div
                    v-for="signature in signatures"
                    :key="signature.id"
                >
                    <div style="display: inline-block;margin-bottom: 20px">
                        <kgr-icon-active :disabled="!signature.signed" small style="vertical-align: sub;margin-right:5px" />
                        <span class="kgr-text--bold">{{ $t('Unterschrift (Seite {page})', { page: signature.coordinates.pageIndex + 1 }) }}</span>:
                    </div>
                    <div style="float:right">
                        <template v-if="signature.signed">
                            {{ $t('unterschrieben am {date} von {signee}', { date: signature.signedAt.toLocaleString(), signee: signature.actor.name }) }}
                        </template>
                        <template v-else-if="signature.declined">
                            {{ $t('verweigert am {date} von {signee}', { date: signature.declinedAt.toLocaleString(), signee: signature.actor.name }) }}
                        </template>
                        <template v-else>
                            {{ $t('wurde noch nicht unterschrieben') }}
                        </template>
                    </div>
                </div>
            </span>
            <template #footer>
                <kgr-button
                    :label="$t('Schließen')"
                    @click="closeInfo"
                    :value="$t('close')"
                />
            </template>
        </kgr-generic-modal>
    </div>
</template>

<script>
import GenericModal from '../../common/modal/GenericModal.vue';
import Icon from '../../common/icon/Icon.vue';
import IconActive from '../../common/icon/IconActive.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import Loading from '../../common/Loading.vue';

export default {
    components: {
        'kgr-loading': Loading,
        'kgr-generic-modal': GenericModal,
        'kgr-icon-active': IconActive,
        'kgr-button': KgrButton,
        'kgr-icon': Icon,
    },
    props: {
        signatures: {
            type: Array,
            required: true,
        },
        view: {
            type: String,
            validate(val) {
                return val === 'employee' || val === 'custody';
            },
            required: true,
        },
        numAvailableSignees: {
            type: Number,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            showInfo: false,
            infoLoaded: false,
        };
    },
    computed: {
        iconClass() {
            switch (this.view) {
                case 'employee':
                    return 'headset';
                case 'custody':
                    return 'user';
            }
            return null;
        },
        completed() {
            if (this.view === 'custody') {
                return this.signatures.filter((s) => !s.signed && !s.declined && s.isSignableByCustody()).length === 0;
            }
            return this.signatures.filter((s) => !s.signed && !s.declined && s.isSignableByEmployee()).length === 0;
        },
    },
    methods: {
        tooltip() {
            let signedSignatures; let totalSignatures = 0;
            const signeesWarning = this.numAvailableSignees === 0 ? `${this.$t('Der Vertrag kann erst unterschrieben werden, wenn mindestens eine Bezugsperson als sorgeberechtigt definiert wurde.')} ` : '';
            switch (this.view) {
                case 'employee':
                    signedSignatures = this.signatures.filter((s) => s.signed && s.isSignableByEmployee()).length;
                    totalSignatures = this.signatures.filter((s) => s.isSignableByEmployee()).length;
                    return signeesWarning + this.$t('{signedSignatures} von {totalSignatures} Unterschriften der Mitarbeiter wurden getätigt.', { signedSignatures, totalSignatures });
                case 'custody':
                    signedSignatures = this.signatures.filter((s) => s.signed && s.isSignableByCustody()).length;
                    totalSignatures = this.signatures.filter((s) => s.isSignableByCustody()).length;
                    return signeesWarning + this.$t('{signedSignatures} von {totalSignatures} Unterschriften der Sorgeberechtigten wurden getätigt.', { signedSignatures, totalSignatures });
                default:
                    return signeesWarning;
            }
        },
        modalHeadline() {
            switch (this.view) {
                case 'employee':
                    return this.$t('Unterschrift(en) Mitarbeiter');
                case 'custody':
                    return this.$t('Unterschrift(en) Sorgeberechtigte');
                default:
                    return '';
            }
        },
        closeInfo(event) {
            this.showInfo = false;
            event.stopPropagation();
        },
        loadInfo() {
            this.showInfo = true;
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.no-signees-available {
    color: var(--kgr-color-warning);
}

.signature-status-box {
    display: inline-block;
    width: 6px;
    height: 20px;
    border-radius: 10px;
    border: 1px solid #ccc;
    margin-right: 2px;
    vertical-align: bottom;
    position: relative;
}
.signature-status {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
}
.signature-status-signed {
    background-color: #4caf50;
}
.signature-status-declined {
    background-color: #ffb400;
}
.signature-status-blank {
    background-color: #ddd;
}
</style>
